import { Flex, rem } from "@mantine/core";
import { Select } from "src/components/shared/MantineCombobox";
import { useDisclosure } from "@mantine/hooks";
import { TimeInput as MantineTimeInput } from "@mantine/dates";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useController, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  changeTimeZone,
  setTriggerInlineSave,
} from "src/redux/slices/generatorSlice";

import TimeInput from "../../components/shared/TimeInput/TimeInput";
import useTriggerInlineSave from "../../hooks/useTriggerInlineSave";
import { setEditing } from "../../redux/slices/entrySlice";
import useAmbNewVoyage from "../hooks/useAmbNewVoyage";
import { getFeatureKey } from "../CustomInput/CustomInput.utils";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import ShipsTimeInfoModal from "./ShipsTimeInfoModal";
import { OVERLAY_PREF } from "../constants";
import { timeRegex } from "../utils/datetime.utils";
import { ICol } from "src/interfaces";

export interface ITimeInputWithTimeZone extends ICol {
  value: IValue | string;
  id: string;
  idx?: number | undefined;
  required?: boolean;
  isLTorUTCOnly?: boolean;
  utcOnly?: boolean;
  hideTimezone?: boolean;
  times?: string;
  domId?: string;
  isOverlay?: boolean;
}

interface Props extends ITimeInputWithTimeZone {
  error?: string;
  onTimeChange?: (e: any) => void;
  sectionIndex?: number;
  className?: string;
  class?: string;
  rowIndex?: number;
  forceValue?: string | false;
  tag?: string;
}

interface IValue {
  type: string;
  time: string;
}

const TimeInputWithTimeZone = ({
  value,
  id,
  idx,
  required = false,
  isLTorUTCOnly,
  utcOnly,
  hideTimezone,
  times,
  domId,
  error,
  onTimeChange,
  sectionIndex,
  isOverlay,
  className,
  class: category,
  rowIndex,
  forceValue = false,
  tag,
  ...col
}: Props) => {
  const dispatch = useDispatch();
  const triggerSave = useTriggerInlineSave(sectionIndex);
  const [opened, { open, close }] = useDisclosure();
  const { timeZone: globalSuffix, features } = useSelector(
    state => state.generatorSlice,
  );
  const { onKeyDown } = useLogbookRefsContext(id);

  const withSeconds = Boolean(col?.NMEAId);
  const { control, setValue, getValues } = useFormContext();
  const timeDefaultValue =
    typeof value === "string" ? value : value?.time || "";

  const ref = useRef<HTMLInputElement>(null);

  const typeDefaultValue = useMemo(
    () =>
      forceValue
        ? forceValue
        : utcOnly
          ? "UTC"
          : (globalSuffix ??
            (isLTorUTCOnly
              ? "LT"
              : typeof value === "string"
                ? globalSuffix
                : value?.type)),
    [forceValue, utcOnly, globalSuffix, isLTorUTCOnly, value],
  );

  function validateTime(timeString: string, required: boolean): boolean {
    // If not required and no time provided, always return true
    if (!required && (!timeString || timeString.trim() === "")) {
      return true;
    }

    // If required, check for existence and correct format
    if (required && (!timeString || typeof timeString !== "string")) {
      return false;
    }

    // Check for HH:MM or HH:MM:SS format (24-hour)
    return timeRegex.test(timeString);
  }

  const {
    field: { onChange, ...timeField },
    fieldState: { isTouched, invalid },
  } = useController({
    name: `${id}.time`,
    control,
    defaultValue: timeDefaultValue,
    rules: {
      required: id.includes(OVERLAY_PREF) ? false : required === true,
      validate: (v: string) => {
        if (id.includes(OVERLAY_PREF)) return true;
        return validateTime(v || "", required);
      },
    },
  });

  const {
    field: { onChange: onTimeZoneChange, ...timeZoneField },
  } = useController({
    name: `${id}.type`,
    control,
    defaultValue: typeDefaultValue,
  });

  // Use ambulance event listeners
  const { onVoyageChange, modalElement } = useAmbNewVoyage({
    tag,
    getValues,
    setValue,
    rowIndex,
  });

  useEffect(() => {
    if ((idx !== 0 || isLTorUTCOnly) && !isOverlay) {
      const newValue = forceValue
        ? forceValue
        : utcOnly
          ? "UTC"
          : (globalSuffix ?? (isLTorUTCOnly ? "LT" : ""));
      if (newValue !== timeZoneField.value) {
        onTimeZoneChange(newValue);
      }
    }
  }, [
    forceValue,
    forceValue,
    globalSuffix,
    idx,
    utcOnly,
    isLTorUTCOnly,
    timeZoneField.value,
    isOverlay,
    onTimeZoneChange,
  ]);

  useEffect(() => {
    if (isOverlay) return;
    const featureKey = getFeatureKey(domId, idx);
    if (featureKey && features[featureKey] && !timeField.value) {
      setValue(`${id}.time`, features[featureKey]);
    }
  }, [idx, domId, id, features, isOverlay]);

  const handleTimeZoneChange = (timezone: string) => {
    triggerSave();
    onTimeZoneChange(timezone);
    if (isLTorUTCOnly) return;
    dispatch(changeTimeZone(timezone));
    open();
  };

  const handleTimeChange = useCallback(
    (value: any) => {
      onChange(value);
      onTimeChange && onTimeChange(value);
      onVoyageChange(value);
    },
    [onChange, onTimeChange, onVoyageChange],
  );

  const maxTime = times ? (times === "first12" ? "13:00" : "24:01") : undefined;
  const minTime = times ? (times === "first12" ? "00:00" : "13:00") : undefined;

  // Determine if we should show error state
  const shouldShowError = invalid && (isTouched || !timeField.value);

  // if (isOverlay) return null;
  return (
    <>
      {modalElement}
      <ShipsTimeInfoModal
        opened={isLTorUTCOnly ? false : opened}
        onClose={close}
      />
      <Flex gap={5}>
        <TimeInput
          value={timeField.value}
          onInputChange={handleTimeChange}
          onKeyDown={onKeyDown}
          maxTime={maxTime}
          minTime={minTime}
          withSeconds={withSeconds}
          className={[
            className ?? "",
            category ?? "",
            `rowIndex-${rowIndex ?? ""}`,
            tag ?? "",
          ]
            .filter(cn => cn !== "")
            .join(" ")}
          dataId={id}
          id={id}
          error={shouldShowError}
        />

        {!hideTimezone && (
          <Select
            styles={{
              input: { width: rem(65), padding: "6px" },
            }}
            {...timeZoneField}
            value={forceValue ? forceValue : timeZoneField.value}
            onChange={handleTimeZoneChange}
            disabled={idx !== 0 || isLTorUTCOnly || timeZoneField.disabled}
            data={["LT", "UTC"]}
          />
        )}
      </Flex>
    </>
  );
};
export default TimeInputWithTimeZone;
